import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import i18n from '../../i18n';
import apiManage from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import CardView from '../../component/card/card-view';
import TableComponent from '../../component/table/table.component';
import MainPageState from '../main/main-page-states';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import DialogComponent from '../../component/dialog/dialog.component';
import { getRegisterUrl, isTicketBAIArea, isVerifactuArea } from '../../constants/spain-city-area';
import ButtonComponent from '../../component/button/button.component';
import { PrivacyPolicy, ShareLink } from '../../request/api/restaurant';
import { ContentCopy } from '@mui/icons-material';
import copy from 'copy-to-clipboard';
import { dateFormat } from '../../constants/utils';
import PdfPreviewDialog from '../../component/pdf-preview-dialog';

const mainPageData = MobxObjectsManager.get(MainPageState);

export default function FiskalySetting() {
    // string_id
    const [stringId, setStringId] = useState<string>('');
    // 加载状态
    const [loading, setLoading] = useState(false);
    // 上传文件loading
    const [fileLoading, setFileLoading] = useState(false)
    // 预览
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    // 文件输入
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // 分享链接弹窗
    const [shareLinkOpen, setShareLinkOpen] = useState(false);
    // 分享链接数据
    const [shareLink, setShareLink] = useState<ShareLinkData>({});
    // 数据
    const [data, setData] = useState<PrivacyPolicy>({});
    // 是否显示Verifactu相关设置
    const [showVerifactu, setShowVerifactu] = useState(false);
    // 是否显示TicketBAI相关设置
    const [showTicketBAI, setShowTicketBAI] = useState(false);
    // 证书注册地址
    const [certificateRegisterUrl, setCertificateRegisterUrl] = useState('')

    useEffect(() => {
        if (mainPageData.currentRestaurant) {
            if (mainPageData?.currentRestaurant?.string_id) {
                setLoading(true);
                setStringId(mainPageData?.currentRestaurant?.string_id);
                initAgreement(mainPageData?.currentRestaurant?.string_id);
            }
        }
    }, [mainPageData.currentRestaurant])

    /**
     * 初始化隐私协议
     * @param value 
     */
    const initAgreement = async (value: string) => {
        try {
            const res = await apiManage.get_privacy_policy(value);
            if (res && !res.error) {
                setData(res)
                // 只有Verifactu 才显示
                setShowVerifactu(isVerifactuArea(res.reg_territory || ''));
                // 只有 setShowTicketBAI才显示
                setShowTicketBAI(isTicketBAIArea(res.reg_territory || ''));
                // 证书注册地址
                setCertificateRegisterUrl(getRegisterUrl(res.reg_territory || ''))
            } else {
                setShowVerifactu(false)
                setShowTicketBAI(false)
                setData({})
            }
        } catch (_e) {
            // 忽略初始化错误，只在交互时提示
        } finally {
            setLoading(false);
        }
    };



    /**
     * 下载纳税人协议 PDF；协议 URL 为空或仅空白时提示联系管理员。
     * @param value - 协议文件 URL（未签名或已签名）
     */
    const handleDownload = async (value: string) => {
        try {
            // 协议未生成时后端可能返回空字符串，trim 后拦截避免无效 fetch
            if (!value.trim()) {
                GlobalNotification.error(i18n.t('fiskaly_agreement_not_generated_contact_admin'));
                return;
            }
            // 1. 获取 PDF 数据
            const response = await fetch(value);
            if (!response.ok) throw new Error('网络错误');

            // 2. 转为 Blob
            const blob = await response.blob();

            // 3. 创建临时下载链接
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'Agreement'; // 指定文件名

            // 4. 触发点击
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 5. 释放内存
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('PDF 下载失败:', error);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    /**
     * 点击上传
     */
    const handleClickUpload = () => {
        fileInputRef.current?.click();
    };

    /**
     * 上传文件
     */
    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // 允许重复选择同一文件
        event.target.value = '';
        if (!file) return;
        if (file.type !== 'application/pdf') {
            GlobalNotification.error(i18n.t('input_rule_text_please_upload_the_correct_picture_format_support_only_x', { x: 'PDF' }));
            return;
        }
        try {
            setFileLoading(true);
            const res = await apiManage.upload_fiskaly_agreement?.(stringId, file);
            if (res && !res.error && res?.url) {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            } else {
                const details = res?.result?.details;
                const message = typeof details === 'string' ? details : i18n.t('global_text_operate_failure');
                GlobalNotification.error(message);
            }
            // 重新获取隐私协议
            initAgreement(stringId);
        } catch (_e) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setFileLoading(false);
        }
    };

    /**
     * 打开 PDF 预览弹窗。
     * @param url - PDF 文件 URL
     * @param emptyErrorKey - URL 为空时的 i18n key；纳税人协议场景与下载一致，责任声明等传 general_text_no_data
     */
    const openPreview = (url: string, emptyErrorKey = 'fiskaly_agreement_not_generated_contact_admin') => {
        if (!url.trim()) {
            GlobalNotification.error(i18n.t(emptyErrorKey));
            return;
        }
        setPreviewUrl(url.trim());
        setPreviewOpen(true);
    };


    /**
     * 生成 Fiskaly 商家声明分享链接；未签名协议未生成时不请求接口。
     */
    const generateShareLink = async () => {
        // 未签名协议 URL 为空表示注册流程未完成，无法对外分享
        const unsignedAgreementUrl = (data.restaurant_unsigned_agreement || '').trim();
        if (!unsignedAgreementUrl) {
            GlobalNotification.error(i18n.t('fiskaly_agreement_not_generated_contact_admin'));
            return;
        }
        const result = await apiManage.post_share_link(stringId);
        if (result && !result.error) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            setShareLinkOpen(true);
            setShareLink({
                expireAt: result.expireAt,
                password: result.password,
                shareUrl: result.shareUrl,
            });
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    // 责任声明表头
    const headCellList = [
        {
            id: 'version',
            label: i18n.t('fiskaly_privacy_version_no'),
        },
        {
            id: 'preview',
            label: i18n.t('general_text_operating'),
            renderFunction: (row) => {
                return (
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={() => openPreview(row.responsibility_declaration || '', 'general_text_no_data')}
                    >
                        {i18n.t('general_text_preview')}
                    </Button>
                );
            }
        }
    ]

    // 分享链接表头
    const shareLinkHeadCellList = [
        {
            id: 'expireAt',
            label: i18n.t('fiskaly_share_link_expire_time'),
            renderFunction: (row: ShareLinkData) => {
                return (
                    <Box>
                        {dateFormat(row?.expireAt, 'YYYY-MM-DD HH:mm:ss')}
                    </Box>
                );
            }
        },
        {
            id: 'password',
            label: i18n.t('login_page_text_password'),
            renderFunction: (row: ShareLinkData) => {
                return (<Box>
                    {row?.password}
                    <ButtonComponent
                        variant='text'
                        handleClick={() => {
                            if (!row?.password) {
                                GlobalNotification.error(i18n.t('general_text_no_data'));
                                return;
                            }
                            const res = copy(row?.password);
                            if (res) GlobalNotification.success(i18n.t('restaurant_page_input_tip_message_copy_successfully'))
                        }}>
                        <ContentCopy />
                    </ButtonComponent>
                </Box>)
            }
        },
        {
            id: 'shareUrl',
            label: i18n.t('fiskaly_share_link_url'),
            renderFunction: (row: ShareLinkData) => {
                return (<Box>
                    <ButtonComponent
                        variant='text'
                        handleClick={() => {
                            if (!row?.shareUrl) {
                                GlobalNotification.error(i18n.t('general_text_no_data'));
                                return;
                            }
                            const res = copy(row?.shareUrl);
                            if (res) GlobalNotification.success(i18n.t('restaurant_page_input_tip_message_copy_successfully'))
                        }}>
                        <ContentCopy />
                    </ButtonComponent>
                </Box>)
            }
        }
    ]



    return (
        <div>
            {
                !loading ?

                    <div>
                        {
                            showVerifactu ?
                                <Box sx={{ maxWidth: 900 }}>
                                    <CardView
                                        title={i18n.t('general_text_fiskaly_agreement')}
                                        contentRender={(
                                            <div>
                                                {i18n.t('restaurant_info_page_status')}:  {data.fiskaly_upload_agreemen === '1' ? i18n.t('fiskaly_setting_status_uploaded') : i18n.t('fiskaly_setting_status_not_uploaded')}
                                            </div>
                                        )}
                                        actionRender={(<Stack direction='row' spacing={2} alignItems='center' mb={2}>
                                            <Button
                                                variant='contained'
                                                onClick={() => handleDownload(data.restaurant_unsigned_agreement || '')}
                                            >
                                                {i18n.t('fiskaly_page_button_download_unsigned_agreement')}
                                            </Button>
                                            <Button
                                                variant='contained'
                                                onClick={handleClickUpload}
                                            >
                                                <>
                                                    {
                                                        fileLoading ?
                                                            <Box style={{
                                                                backgroundColor: '#fff3',
                                                                position: 'absolute',
                                                                top: 0,
                                                                bottom: 0,
                                                                left: 0,
                                                                right: 0,
                                                                zIndex: 1,
                                                                borderRadius: 'inherit',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            }}>
                                                                <CircularProgress
                                                                    color='primary'
                                                                    size={'20px'} />
                                                            </Box>
                                                            :
                                                            null
                                                    }
                                                    {i18n.t('general_text_upload')}
                                                </>
                                            </Button>
                                            {
                                                data.fiskaly_upload_agreemen === '1' &&
                                                <Button
                                                    variant='contained'
                                                    onClick={() => handleDownload(data.restaurant_agreement || '')}
                                                >
                                                    {i18n.t('fiskaly_page_button_download_signed_agreement')}
                                                </Button>
                                            }
                                            <Button
                                                variant='outlined'
                                                onClick={() => openPreview(data.restaurant_agreement || data.restaurant_unsigned_agreement || '')}
                                            >
                                                {i18n.t('general_text_preview')}
                                            </Button>
                                            <ButtonComponent
                                                variant='outlined'
                                                handleClick={generateShareLink}
                                                sx={{ ml: 2 }}
                                            >
                                                {i18n.t('fiskaly_share_link_generate')}
                                            </ButtonComponent>

                                        </Stack>
                                        )}
                                        style={{ marginBottom: 16 }}
                                    />
                                    <CardView
                                        title={i18n.t('fiskaly_privacy_title')}
                                        contentRender={(
                                            <TableComponent
                                                showChecked={0}
                                                list={data?.policy?.responsibility_declaration ? [data.policy] : []}
                                                headCellList={headCellList}
                                            />
                                        )}
                                    />
                                    <input
                                        ref={fileInputRef}
                                        type='file'
                                        accept='application/pdf'
                                        style={{ display: 'none' }}
                                        onChange={handleUpload}
                                    />
                                    <PdfPreviewDialog
                                        open={previewOpen}
                                        onClose={() => setPreviewOpen(false)}
                                        url={previewUrl}
                                    />
                                </Box >
                                :
                                null
                        }
                        {

                            showTicketBAI ?
                                <Box sx={{ maxWidth: 900 }}>
                                    <CardView
                                        title={i18n.t('fiskaly_setting_page_text_device_certificate')}
                                        contentRender={(
                                            <div>
                                                {i18n.t('fiskaly_setting_page_text_serial_number')}:  {data.fiskaly_serial_number || i18n.t('general_text_no_data')}
                                            </div>
                                        )}
                                        actionRender={<>
                                            {
                                                certificateRegisterUrl ?

                                                    <Button
                                                        variant='outlined'
                                                        href={certificateRegisterUrl}
                                                        type='link'
                                                        target='_blank'
                                                    >
                                                        {i18n.t('fiskaly_setting_page_text_certificate_registration')}
                                                    </Button>
                                                    :
                                                    null
                                            }
                                        </>}
                                        style={{ marginBottom: 16 }}
                                    />

                                </Box >
                                :
                                null
                        }
                        {showVerifactu || showTicketBAI ? null :
                            <Box className='FCC' sx={{ minHeight: '50vh' }}>
                                {i18n.t('fiskaly_please_register_first')}
                            </Box>
                        }
                    </div>
                    :
                    <div className='skeleton'>
                        <Skeleton variant='rounded' width={900} height={148} animation='wave' sx={{ mb: 4 }} />
                        <Skeleton variant='rounded' width={900} height={205} animation='wave' />
                    </div>
            }
            {/* 分享链接弹窗 */}
            <DialogComponent
                open={shareLinkOpen}
                onClose={() => setShareLinkOpen(false)}
                maxWidth='sm'
                heightSize='xs'
                fullWidth
                defaultHeaderProps={{ title: i18n.t('google_text_detail') }}
            >
                <Box>
                    <Typography variant='body1' sx={{ mb: 2 }}>
                        {i18n.t('restaurant_info_page_text_share_link_password')}
                    </Typography>
                    <TableComponent
                        showChecked={0}
                        list={[shareLink]}
                        headCellList={shareLinkHeadCellList}
                    />
                </Box>
            </DialogComponent>
        </div>
    );
}


// 分享链接弹窗数据类型
type ShareLinkData = Pick<ShareLink, 'expireAt' | 'shareUrl' | 'password'>

